import React, { useEffect, useState } from "react";
import TestingApi from "../../../../api/TestingApi";
import { ESuiteMode, ETestingState } from "../../../../Model/ModuleState";
import { ETestingType } from "../../../../Model/TestingParameter";
import {
  getImagerTestingState,
  getSuiteMode,
} from "../../../../redux/reducers/ModuleStateReducer";
import { useTypedSelector } from "../../../../redux/reducers/RootReducer";
import ToolsContentButton from "../../../Tools/ToolsCommon/ToolsContentButton";
import ToolsContentTextBox from "../../../Tools/ToolsCommon/ToolsContentTextBox";
import ToolsContentTextCheckBox from "../../../Tools/ToolsCommon/ToolsContentTextCheckBox";

type Props = {
  imagerIdentifier: string;
};

export default function LightPathSwitchingTest({ imagerIdentifier }: Props) {
  const testing = useTypedSelector((state) =>
    getImagerTestingState(state, imagerIdentifier)
  );
  const suiteMode = useTypedSelector((state) => getSuiteMode(state));
  const [status, setStatus] = useState("Count : ");

  const [testParameterValue, setTestParameterValue] = useState({
    reCenterWellEvery: 2,
    bumpPlateEvery: 2,
    captureImageEvery: 2,
    saveCapturedImageEvery: 2,
    runDiagnosticEvery: 2,
  });

  const [testParameterActive, setTestParameterActive] = useState({
    reCenterWellEvery: true,
    bumpPlateEvery: true,
    captureImageEvery: true,
    saveCapturedImageEvery: true,
    runDiagnosticEvery: true,
  });

  useEffect(() => {
    if (testing.lightPathSwitching?.state === ETestingState.Started) {
      const counter = testing.lightPathSwitching?.counter ?? 0;
      setStatus("Count : " + counter.toString());
    }
  }, [testing.lightPathSwitching?.counter]);

  useEffect(() => {
    if (testing.lightPathSwitching?.state === ETestingState.Started) {
      const parameter = testing.lightPathSwitching.parameter;

      setTestParameterValue({
        reCenterWellEvery:
          parameter?.reCenterWellEvery ?? testParameterValue.reCenterWellEvery,
        bumpPlateEvery:
          parameter?.bumpPlateEvery ?? testParameterValue.bumpPlateEvery,
        captureImageEvery:
          parameter?.captureImageEvery ?? testParameterValue.captureImageEvery,
        saveCapturedImageEvery:
          parameter?.saveCapturedImageEvery ??
          testParameterValue.saveCapturedImageEvery,
        runDiagnosticEvery:
          parameter?.runDiagnosticEvery ??
          testParameterValue.runDiagnosticEvery,
      });
    }
  }, [
    testing.lightPathSwitching,
    testing.lightPathSwitching?.parameter?.reCenterWellEvery,
    testing.lightPathSwitching?.parameter?.bumpPlateEvery,
    testing.lightPathSwitching?.parameter?.captureImageEvery,
    testing.lightPathSwitching?.parameter?.saveCapturedImageEvery,
    testing.lightPathSwitching?.parameter?.runDiagnosticEvery,
  ]);

  const runTest = () => {
    TestingApi.doTesting({
      moduleIdentifier: imagerIdentifier,
      type: ETestingType.LightPathSwitching,
      lightPathSwitchingTest: {
        reCenterWellEvery: testParameterActive.reCenterWellEvery
          ? testParameterValue.reCenterWellEvery
          : undefined,
        bumpPlateEvery: testParameterActive.bumpPlateEvery
          ? testParameterValue.bumpPlateEvery
          : undefined,
        captureImageEvery: testParameterActive.captureImageEvery
          ? testParameterValue.captureImageEvery
          : undefined,
        saveCapturedImageEvery: testParameterActive.saveCapturedImageEvery
          ? testParameterValue.saveCapturedImageEvery
          : undefined,
        runDiagnosticEvery: testParameterActive.runDiagnosticEvery
          ? testParameterValue.runDiagnosticEvery
          : undefined,
      },
    });
  };

  const stopTest = () => {
    TestingApi.stopTesting();
  };

  const isDisabled = suiteMode !== ESuiteMode.Normal;
  const isRunningLightPathSwitchingTest =
    suiteMode === ESuiteMode.Testing &&
    testing.lightPathSwitching?.state === ETestingState.Started;

  return (
    <div className={"testing-content"}>
      <ToolsContentTextCheckBox
        disabled={isDisabled}
        id="imageEveryCam"
        checked={testParameterActive.captureImageEvery}
        label="Image Every"
        checkBoxColorVariant="alternate"
        value={testParameterValue.captureImageEvery}
        onClickRadio={() =>
          setTestParameterActive({
            ...testParameterActive,
            captureImageEvery: !testParameterActive.captureImageEvery,
          })
        }
        onChangeValue={(value) => {
          setTestParameterValue({
            ...testParameterValue,
            captureImageEvery: +value,
          });
        }}
        minValueInclusive={1}
        style={{ margin: "0px", height: 48 }}
      />

      <ToolsContentTextCheckBox
        disabled={isDisabled}
        id="saveCapturedImageEvery"
        checked={testParameterActive.saveCapturedImageEvery}
        label="Save Image Every"
        checkBoxColorVariant="alternate"
        value={testParameterValue.saveCapturedImageEvery}
        onClickRadio={() =>
          setTestParameterActive({
            ...testParameterActive,
            saveCapturedImageEvery: !testParameterActive.saveCapturedImageEvery,
          })
        }
        onChangeValue={(value) => {
          setTestParameterValue({
            ...testParameterValue,
            saveCapturedImageEvery: +value,
          });
        }}
        minValueInclusive={1}
        style={{ margin: "0px", height: 48 }}
      />

      <ToolsContentTextCheckBox
        disabled={isDisabled}
        id="runDiagnosticEvery"
        checked={testParameterActive.runDiagnosticEvery}
        label="Diagnostic Every"
        checkBoxColorVariant="alternate"
        value={testParameterValue.runDiagnosticEvery}
        onClickRadio={() =>
          setTestParameterActive({
            ...testParameterActive,
            runDiagnosticEvery: !testParameterActive.runDiagnosticEvery,
          })
        }
        onChangeValue={(value) => {
          setTestParameterValue({
            ...testParameterValue,
            runDiagnosticEvery: +value,
          });
        }}
        minValueInclusive={1}
        style={{ margin: "0px", height: 48 }}
      />
      <ToolsContentTextCheckBox
        disabled={isDisabled}
        id="reCenterWellEvery"
        checked={testParameterActive.reCenterWellEvery}
        label="Recenter Well Every"
        checkBoxColorVariant="alternate"
        value={testParameterValue.reCenterWellEvery}
        onClickRadio={() =>
          setTestParameterActive({
            ...testParameterActive,
            reCenterWellEvery: !testParameterActive.reCenterWellEvery,
          })
        }
        onChangeValue={(value) => {
          setTestParameterValue({
            ...testParameterValue,
            reCenterWellEvery: +value,
          });
        }}
        minValueInclusive={1}
        style={{ margin: "0px", height: 48 }}
      />

      <div className={"testing-button-group"}>
        <ToolsContentButton
          text="Stop"
          onClick={stopTest}
          disabled={!isRunningLightPathSwitchingTest}
          variant="primary"
          colorVariant="black"
        />
        <ToolsContentButton
          text="run"
          onClick={runTest}
          disabled={isDisabled}
          variant="primary"
          colorVariant="default"
        />
      </div>

      <div
        className={"testing-status-box"}
        style={{
          opacity: isDisabled ? 0.5 : 1,
        }}
      >
        <p>{status}</p>
      </div>
    </div>
  );
}
